import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { indexStructure, useOrgSearch, useOrgStructure } from '../api/org';
import { Icon } from '../components/Icon';
import { ErrorState, LoadingState } from '../components/states';
import { usePageTitle } from '../layout/shellContext';
import { OrgChart } from './org/OrgChart';
import { OrgTable } from './org/OrgTable';
import { SearchResults } from './org/SearchResults';
import { UnitPanel } from './org/UnitPanel';

export function OrgPage() {
  usePageTitle('Оргструктура');
  const structure = useOrgStructure();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const search = useOrgSearch(deferredQuery);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(params.get('department'));
  const [focusId, setFocusId] = useState<string | null>(params.get('department'));
  const [fullscreen, setFullscreen] = useState(false);

  const index = useMemo(() => (structure.data ? indexStructure(structure.data) : null), [structure.data]);
  const rootId = structure.data?.rootIds[0] ?? null;
  const currentId = selectedId && index?.byId.has(selectedId) ? selectedId : rootId;

  // Корень раскрыт сразу; путь к выбранному подразделению раскрывается.
  useEffect(() => {
    if (!index || !currentId) return;
    setExpanded((previous) => {
      const next = new Set(previous);
      // Первое раскрытие меняет ширину схемы: после него показываем выбранный узел заново.
      if (!previous.size) setFocusId((focus) => focus ?? currentId);
      if (rootId) next.add(rootId);
      for (const item of index.pathTo(currentId).slice(0, -1)) next.add(item.id);
      return next.size === previous.size ? previous : next;
    });
  }, [index, currentId, rootId]);

  // Переход из карточки сотрудника: /org?department=<id>
  const departmentParam = params.get('department');
  useEffect(() => {
    if (departmentParam) {
      setSelectedId(departmentParam);
      setFocusId(departmentParam);
      setView('chart');
      setQuery('');
    }
  }, [departmentParam]);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [fullscreen]);

  const select = useCallback(
    (id: string, focus = false) => {
      setSelectedId(id);
      if (focus) setFocusId(id);
      if (params.get('department')) {
        setParams((previous) => {
          const next = new URLSearchParams(previous);
          next.delete('department');
          return next;
        });
      }
    },
    [params, setParams],
  );
  const toggle = useCallback((id: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (structure.isPending) return <LoadingState label="Загружаю оргструктуру" />;
  if (structure.isError)
    return <ErrorState error={structure.error} onRetry={() => void structure.refetch()} />;
  if (!index || !currentId) {
    return (
      <ErrorState title="Оргструктура пуста" error={new Error('Сотрудники ещё не загружены из Bitrix24.')} />
    );
  }
  const department = index.byId.get(currentId);
  const searching = query.trim().length >= 2;

  return (
    <div className="page">
      <div className="panel org-toolbar">
        <label className="search-box">
          <Icon name="search" />
          <span className="visually-hidden">Поиск</span>
          <input
            type="search"
            placeholder="Поиск: ФИО, должность, город или подразделение"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <fieldset className="pill-row">
          <legend className="visually-hidden">Вид</legend>
          <button
            type="button"
            className="pill-toggle"
            aria-pressed={view === 'chart'}
            onClick={() => setView('chart')}
          >
            <Icon name="network" size={14} />
            Схема
          </button>
          <button
            type="button"
            className="pill-toggle"
            aria-pressed={view === 'table'}
            onClick={() => setView('table')}
          >
            <Icon name="list" size={14} />
            Таблица
          </button>
          {!searching ? (
            <>
              <button
                type="button"
                className="pill-toggle"
                onClick={() => setExpanded(new Set(structure.data.departments.map((item) => item.id)))}
              >
                Развернуть всё
              </button>
              <button
                type="button"
                className="pill-toggle"
                onClick={() => {
                  setExpanded(new Set(rootId ? [rootId] : []));
                  if (rootId) select(rootId, true);
                }}
              >
                Свернуть
              </button>
            </>
          ) : null}
        </fieldset>
      </div>

      {searching ? (
        search.data ? (
          <SearchResults
            result={search.data}
            onOpenDepartment={(id) => {
              setQuery('');
              setView('chart');
              select(id, true);
            }}
          />
        ) : search.isError ? (
          <ErrorState error={search.error} />
        ) : (
          <LoadingState label="Ищу" />
        )
      ) : view === 'table' ? (
        <OrgTable
          structure={structure.data}
          byId={index.byId}
          expanded={expanded}
          onToggle={toggle}
          onOpenInChart={(id) => {
            setView('chart');
            select(id, true);
          }}
        />
      ) : (
        <div className={`org-layout${fullscreen ? ' fullscreen' : ''}`}>
          <OrgChart
            structure={structure.data}
            byId={index.byId}
            selectedId={currentId}
            expanded={expanded}
            focusId={focusId}
            fullscreen={fullscreen}
            onSelect={(id) => {
              select(id);
              if (!expanded.has(id)) toggle(id);
            }}
            onToggle={toggle}
            onFullscreen={() => {
              setFullscreen((value) => !value);
              setFocusId(currentId);
            }}
            onFocused={() => setFocusId(null)}
          />
          {department ? (
            <UnitPanel
              structure={structure.data}
              department={department}
              path={index.pathTo(currentId)}
              onSelect={(id) => select(id, true)}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
